/**
 * Measured calculator.svg scale (center 256.004, 295.702).
 * 0° = 12 o'clock, clockwise+. Decade starts at 10 (top), not RATE·60.
 * Tick angles from #Tick_Marks_-_Outer_Ring long ticks, matched to log10(v/10).
 */

export const CALCULATOR_CENTER = { x: 256.004, y: 295.702 };

/** Outer tick mid / inner tick tip — unit indexes sit on these rings. */
export const CALCULATOR_OUTER_INDEX_R = 221;
export const CALCULATOR_INNER_INDEX_R = 209;
/** Blue conversion arrows sit on the tick tips, just inside the printed word. */
export const CALCULATOR_OUTER_ARROW_R = 228;
export const CALCULATOR_INNER_ARROW_R = 202;
/** Decade numerals. */
export const CALCULATOR_OUTER_NUMERAL_R = 232;
export const CALCULATOR_INNER_NUMERAL_R = 198;

/** Major printed values → measured outer-tick angle (deg). */
export const CALCULATOR_TICKS: ReadonlyArray<{ value: number; ang: number }> = [
  { value: 10, ang: 359.74 },
  { value: 11, ang: 14.45 },
  { value: 12, ang: 27.91 },
  { value: 13, ang: 41.51 },
  { value: 14, ang: 53.02 },
  { value: 15, ang: 62.73 },
  { value: 16, ang: 72.83 },
  { value: 17, ang: 82.63 },
  { value: 18, ang: 91.44 },
  { value: 19, ang: 99.99 },
  { value: 20, ang: 108.09 },
  { value: 21, ang: 115.84 },
  { value: 22, ang: 123.15 },
  { value: 23, ang: 129.93 },
  { value: 24, ang: 136.7 },
  { value: 25, ang: 143.19 },
  { value: 26, ang: 149.52 },
  { value: 27, ang: 155.37 },
  { value: 28, ang: 161.08 },
  { value: 29, ang: 166.42 },
  { value: 30, ang: 171.87 },
  { value: 32, ang: 181.87 },
  { value: 34, ang: 191.26 },
  { value: 35, ang: 195.86 },
  { value: 36, ang: 200.19 },
  { value: 38, ang: 208.71 },
  { value: 40, ang: 216.75 },
  { value: 42, ang: 224.35 },
  { value: 44, ang: 231.75 },
  { value: 45, ang: 235.19 },
  { value: 46, ang: 238.85 },
  { value: 48, ang: 245.52 },
  { value: 50, ang: 251.75 },
  { value: 55, ang: 266.5 },
  { value: 60, ang: 280.09 },
  { value: 65, ang: 292.65 },
  { value: 70, ang: 304.27 },
  { value: 75, ang: 314.97 },
  { value: 80, ang: 324.99 },
  { value: 85, ang: 334.36 },
  { value: 90, ang: 343.4 },
  { value: 95, ang: 351.77 },
];

export type CalculatorLabel = { ang: number; r: number };

export type CalculatorCaption = {
  /** Scale mantissa at the conversion index (not always the printed-word centroid). */
  value: number;
  name: string;
  outerLabel: CalculatorLabel | null;
  innerLabel: CalculatorLabel | null;
};

/**
 * Every Mini_Text / Small_Outer_Text caption, both rings.
 * Positions are path-cluster centroids (translate −32.9 already applied).
 */
export const CALCULATOR_UNITS: Record<string, CalculatorCaption> = {
  imp_gal: {
    value: 13.1 * 0.832674,
    name: 'IMP. GAL',
    outerLabel: { ang: 9.38, r: 247.6 },
    innerLabel: { ang: 9.2, r: 189.9 },
  },
  km: {
    value: 12.15,
    name: 'KM',
    outerLabel: { ang: 30.45, r: 247.5 },
    innerLabel: { ang: 33.02, r: 189.6 },
  },
  us_gal: {
    value: 13.1,
    name: 'US GAL',
    outerLabel: { ang: 38.65, r: 248.3 },
    innerLabel: { ang: 46.71, r: 189.0 },
  },
  ft: {
    value: 14.23,
    name: 'FT',
    outerLabel: { ang: 55.17, r: 248.0 },
    innerLabel: null,
  },
  fuel: {
    value: 17.41,
    name: 'FUEL',
    outerLabel: { ang: 86.68, r: 247.0 },
    innerLabel: null,
  },
  kg: {
    value: 16.54,
    name: 'KG',
    outerLabel: null,
    innerLabel: { ang: 78.64, r: 187.7 },
  },
  tas: {
    value: 26.82,
    name: 'TAS',
    outerLabel: { ang: 154.23, r: 246.6 },
    innerLabel: null,
  },
  cas: {
    value: 27.55,
    name: 'CAS',
    outerLabel: null,
    innerLabel: { ang: 158.45, r: 188.1 },
  },
  hours: {
    value: 33.18,
    name: 'HOURS',
    outerLabel: null,
    innerLabel: { ang: 187.5, r: 165.0 },
  },
  lbs: {
    value: 36.57,
    name: 'LBS',
    outerLabel: { ang: 202.71, r: 248.9 },
    innerLabel: null,
  },
  seconds: {
    value: 36.47,
    name: 'SECONDS',
    outerLabel: null,
    innerLabel: { ang: 202.3, r: 188.8 },
  },
  meters: {
    value: 43.1,
    name: 'METERS',
    outerLabel: null,
    innerLabel: { ang: 228.4, r: 190.1 },
  },
  liters: {
    value: 13.1 * 3.785,
    name: 'LITERS',
    outerLabel: { ang: 247.21, r: 248.9 },
    innerLabel: { ang: 246.06, r: 190.6 },
  },
  true_alt: {
    value: 52.51,
    name: 'TRUE ALT',
    outerLabel: { ang: 259.3, r: 249.0 },
    innerLabel: null,
  },
  cal_alt: {
    value: 52.72,
    name: 'CAL ALT',
    outerLabel: null,
    innerLabel: { ang: 259.91, r: 190.1 },
  },
  distance: {
    value: 63.48,
    name: 'DISTANCE',
    outerLabel: { ang: 288.96, r: 248.9 },
    innerLabel: null,
  },
  time: {
    value: 63.91,
    name: 'TIME',
    outerLabel: null,
    innerLabel: { ang: 289.99, r: 191.1 },
  },
  naut: {
    value: 67.15,
    name: 'NAUT',
    outerLabel: { ang: 297.73, r: 245.2 },
    innerLabel: null,
  },
  stat: {
    value: 75.03,
    name: 'STAT',
    outerLabel: { ang: 315.09, r: 245.1 },
    innerLabel: null,
  },
  statute: {
    value: 76.26,
    name: 'STATUTE',
    outerLabel: null,
    innerLabel: { ang: 317.64, r: 191.5 },
  },
  fuel_lbs: {
    value: 77.76,
    name: 'FUEL LBS',
    outerLabel: { ang: 320.68, r: 250.5 },
    innerLabel: null,
  },
  oil_lbs: {
    value: 10,
    name: 'OIL LBS',
    outerLabel: { ang: 353.93, r: 248.8 },
    innerLabel: null,
  },
  temp_conv: {
    value: 31.95,
    name: 'TEMPERATURE CONVERSION SCALE',
    outerLabel: { ang: 181.6, r: 245.3 },
    innerLabel: null,
  },
  rate: {
    value: 60,
    name: 'RATE',
    outerLabel: { ang: 280.09, r: 230 },
    innerLabel: { ang: 280.09, r: 209 },
  },
};

/**
 * Measured blue-arrow centroids (deg). Oppose these, not the scale-value
 * angles — printed arrows sit a degree or two off the conversion mantissa.
 */
export const CALCULATOR_UNIT_ARROWS: Record<string, { outer?: number; inner?: number }> = {
  imp_gal: { outer: 9.79, inner: 9.79 },
  km: { outer: 30.98, inner: 30.98 },
  us_gal: { outer: 38.42 },
  ft: { outer: 55.43 },
  kg: { inner: 79.02 },
  lbs: { outer: 202.62 },
  seconds: { inner: 200.14 },
  meters: { inner: 229.67 },
  liters: { outer: 246.54, inner: 246.54 },
  // NAUT has no #Arrows path. Index is the left edge of the word (N), on the 66 tick.
  naut: { outer: 294.63 },
  statute: { inner: 316.58 },
  oil_lbs: { outer: 353.45 },
};

/** Fixed-base pressure-altitude thousands in the airspeed/altitude windows. */
export const CALCULATOR_PA_TICKS: ReadonlyArray<{ pa: number; ang: number }> = [
  { pa: 0, ang: 35.92 },
  { pa: 5, ang: 50.19 },
  { pa: 10, ang: 65.1 },
  { pa: 15, ang: 80.7 },
  { pa: 20, ang: 97.03 },
  { pa: 25, ang: 114.09 },
  { pa: 30, ang: 131.96 },
  { pa: 35, ang: 150.59 },
  { pa: 40, ang: 170.0 },
  { pa: 45, ang: 190.22 },
  { pa: 50, ang: 211.22 },
];
export const CALCULATOR_PA_R = 123;

/** Mach No. Index arrow on tas-base (fixed). */
export const CALCULATOR_MACH_INDEX = { x: 200.26, y: 188.1, r: 22 };

export const CALCULATOR_UNIT_ALIASES: Record<string, string> = {
  usgal: 'us_gal',
  us_gallons: 'us_gal',
  litre: 'liters',
  litres: 'liters',
  liter: 'liters',
  imp: 'imp_gal',
  impgal: 'imp_gal',
  imperial: 'imp_gal',
  imp_gal: 'imp_gal',
  metre: 'meters',
  metres: 'meters',
  meter: 'meters',
  feet: 'ft',
  nautical: 'naut',
  naut_miles: 'naut',
  statute_miles: 'statute',
  stat_miles: 'stat',
  kilometres: 'km',
  kilometers: 'km',
  kilogram: 'kg',
  kilograms: 'kg',
  fuel_lb: 'fuel_lbs',
  oil_lb: 'oil_lbs',
  oil: 'oil_lbs',
  pound: 'lbs',
  pounds: 'lbs',
  true_altitude: 'true_alt',
  cal_altitude: 'cal_alt',
  calibrated_altitude: 'cal_alt',
  calibrated_airspeed: 'cas',
  true_airspeed: 'tas',
};

export function calculatorMantissa(v: number): number {
  let x = Math.abs(Number(v));
  if (!Number.isFinite(x) || x === 0) return 10;
  while (x < 10) x *= 10;
  while (x >= 100) x /= 10;
  return x;
}

function unwrapTo(from: number, to: number): number {
  let t = to;
  while (t - from < -180) t += 360;
  while (t - from > 180) t -= 360;
  return t;
}

/** Angle of a scale value from the measured tick table (log interpolation). */
export function calculatorAngleOfValue(v: number): number {
  const m = calculatorMantissa(v);
  const ticks = CALCULATOR_TICKS;
  const last = ticks[ticks.length - 1];
  const first = ticks[0];
  const hi = { value: 100, ang: unwrapTo(last.ang, first.ang) };
  let lo = first;
  let hiT = ticks[1] || hi;
  if (m >= last.value) {
    lo = last;
    hiT = hi;
  } else {
    for (let i = 0; i < ticks.length - 1; i++) {
      if (m >= ticks[i].value && m <= ticks[i + 1].value) {
        lo = ticks[i];
        hiT = ticks[i + 1];
        break;
      }
    }
  }
  const a0 = lo.ang;
  const a1 = unwrapTo(a0, hiT.ang);
  const span = Math.log(hiT.value / lo.value);
  const t = span === 0 ? 0 : Math.log(m / lo.value) / span;
  const ang = a0 + t * (a1 - a0);
  return ((ang % 360) + 360) % 360;
}

export function calculatorResolveUnit(mark: string | number | undefined): string {
  const key = String(mark || '')
    .trim()
    .toLowerCase()
    .replace(/[\s.-]+/g, '_');
  return CALCULATOR_UNIT_ALIASES[key] || key;
}

export function calculatorUnitName(mark: string | number | undefined): string {
  const resolved = calculatorResolveUnit(mark);
  return CALCULATOR_UNITS[resolved]?.name || String(mark || '').toUpperCase();
}

export function calculatorUnitValue(mark: string | number | undefined): number | null {
  const u = CALCULATOR_UNITS[calculatorResolveUnit(mark)];
  return u ? u.value : null;
}

/** Printed caption centroid (for the text overlay). */
export function calculatorUnitLabelSpot(
  mark: string,
  ring: 'inner' | 'outer',
): { ang: number; r: number } | null {
  const u = CALCULATOR_UNITS[calculatorResolveUnit(mark)];
  if (!u) return null;
  return (ring === 'outer' ? u.outerLabel : u.innerLabel) || null;
}

/** Printed blue-arrow angle, then caption, then scale-value angle. */
export function calculatorUnitOpposeAngle(
  mark: string,
  ring: 'inner' | 'outer',
): number | null {
  const key = calculatorResolveUnit(mark);
  const arrow = CALCULATOR_UNIT_ARROWS[key]?.[ring];
  if (Number.isFinite(arrow)) return arrow as number;
  const cap = calculatorUnitLabelSpot(mark, ring);
  if (cap) return cap.ang;
  const value = calculatorUnitValue(mark);
  return value != null ? calculatorAngleOfValue(value) : null;
}

function shortestRotation(fromAng: number, toAng: number): number {
  let r = fromAng - toAng;
  r = ((r % 360) + 360) % 360;
  if (r > 180) r -= 360;
  return r;
}

/** Outer unit arrow against inner unit arrow. */
export function calculatorRotationToOpposeUnits(fromUnit: string, toUnit: string): number {
  const fromAng = calculatorUnitOpposeAngle(fromUnit, 'outer');
  const toAng = calculatorUnitOpposeAngle(toUnit, 'inner');
  if (fromAng == null || toAng == null) return 0;
  return shortestRotation(fromAng, toAng);
}

/** Outer scale value against inner scale value (same radial). */
export function calculatorRotationToOpposeValues(outerValue: number, innerValue: number): number {
  return shortestRotation(calculatorAngleOfValue(outerValue), calculatorAngleOfValue(innerValue));
}

export function calculatorPressureAltAngle(paThousands: number): number | null {
  const v = Number(paThousands);
  if (!Number.isFinite(v)) return null;
  const ticks = CALCULATOR_PA_TICKS;
  if (v <= ticks[0].pa) return ticks[0].ang;
  if (v >= ticks[ticks.length - 1].pa) return ticks[ticks.length - 1].ang;
  for (let i = 0; i < ticks.length - 1; i++) {
    if (v >= ticks[i].pa && v <= ticks[i + 1].pa) {
      const t = (v - ticks[i].pa) / (ticks[i + 1].pa - ticks[i].pa);
      return ticks[i].ang + t * (ticks[i + 1].ang - ticks[i].ang);
    }
  }
  return null;
}

export function calculatorPointAtAngle(
  angleDeg: number,
  cx: number,
  cy: number,
  radius: number,
): { x: number; y: number } {
  const rad = (Number(angleDeg) * Math.PI) / 180;
  return { x: cx + radius * Math.sin(rad), y: cy - radius * Math.cos(rad) };
}

/**
 * Circle centred on the printed unit word + its blue arrow (same spoke as the
 * caption, not the nearby decade numeral).
 */
export function calculatorUnitHighlight(
  mark: string,
  ring: 'inner' | 'outer',
  cx: number,
  cy: number,
): { x: number; y: number; r: number } | null {
  const value = calculatorUnitValue(mark);
  const cap = calculatorUnitLabelSpot(mark, ring);
  if (value == null && !cap) return null;
  const arrowR = ring === 'outer' ? CALCULATOR_OUTER_ARROW_R : CALCULATOR_INNER_ARROW_R;
  const ang = calculatorUnitOpposeAngle(mark, ring) ?? cap?.ang ?? calculatorAngleOfValue(value as number);
  const wordR = cap?.r ?? arrowR + (ring === 'outer' ? 18 : -12);
  const word = calculatorPointAtAngle(ang, cx, cy, wordR);
  const tip = calculatorPointAtAngle(ang, cx, cy, arrowR);
  const x = (word.x + tip.x) / 2;
  const y = (word.y + tip.y) / 2;
  const span = Math.hypot(word.x - tip.x, word.y - tip.y) / 2;
  return { x, y, r: Math.max(span + 20, 28) };
}
